/**
 * Layout admin — barre latérale groupée + zone de contenu.
 * Remplace le header/footer public sur toutes les pages /admin.
 *
 * - Sidebar gauche fixe (desktop) : logo, groupes de modules, compte + déconnexion en bas.
 * - Barre supérieure (mobile) : hamburger + titre de la page courante.
 * - Drawer coulissant sur mobile.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { getAdminGroupedNav, findActiveModule } from "@/config/adminModules";
import { useAuth } from "@/lib/auth";
import api, { assetUrl } from "@/lib/api";
import { useSiteContent } from "@/lib/useSiteContent";
import { Menu, X, LogOut, ExternalLink } from "lucide-react";
import { ConfirmProvider } from "@/components/admin/ConfirmProvider";

// Modules qui peuvent porter un badge d'activité (doit correspondre aux clés
// renvoyées par POST /admin/activity-counts côté backend).
const BADGED_MODULES = new Set([
    "quotes", "cee-leads", "isolation-leads", "callbacks", "contact-messages", "mail",
]);
const SEEN_STORAGE_KEY = "em_admin_seen";
const POLL_MS = 60000;

const readSeen = () => {
    try {
        const raw = localStorage.getItem(SEEN_STORAGE_KEY);
        if (raw) return JSON.parse(raw);
        // Première utilisation : on considère tout l'historique comme « déjà vu »,
        // pour ne pas noyer l'admin sous des badges reflétant le passé. Les badges
        // ne compteront que ce qui arrive à partir de maintenant.
        const now = new Date().toISOString();
        const seeded = {};
        BADGED_MODULES.forEach((m) => { seeded[m] = now; });
        localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(seeded));
        return seeded;
    } catch { return {}; }
};
const writeSeen = (seen) => {
    try { localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(seen)); } catch { /* quota/private mode */ }
};

/**
 * Compteurs d'activité de la sidebar. Le badge d'un module = nombre d'éléments
 * arrivés depuis sa dernière visite (horodatage mémorisé en localStorage), ce qui
 * le vide dès qu'on ouvre la section. Pour la boîte mail, c'est le nombre de
 * non-lus IMAP. Sondage toutes les 60 s ; échec silencieux (jamais bloquant).
 */
function useActivityCounts() {
    const [counts, setCounts] = useState({});
    const seenRef = useRef(readSeen());

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.post("/admin/activity-counts", { seen: seenRef.current });
            setCounts(data || {});
        } catch { /* réseau ou session : on retentera au prochain cycle */ }
    }, []);

    useEffect(() => {
        refresh();
        const id = setInterval(refresh, POLL_MS);
        // Rafraîchit aussi au retour sur l'onglet, sans attendre le cycle.
        const onFocus = () => refresh();
        window.addEventListener("focus", onFocus);
        return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
    }, [refresh]);

    // Marque une section comme vue « maintenant » et efface son badge localement.
    const markSeen = useCallback((moduleId) => {
        if (!BADGED_MODULES.has(moduleId)) return;
        seenRef.current = { ...seenRef.current, [moduleId]: new Date().toISOString() };
        writeSeen(seenRef.current);
        setCounts((c) => ({ ...c, [moduleId]: 0 }));
    }, []);

    return { counts, markSeen };
}

function SidebarContent({ onNavigate, counts, markSeen }) {
    const { user, logout, hasPerm } = useAuth();
    const groups = getAdminGroupedNav(hasPerm);
    const c = useSiteContent(["header.v2.logo"]);
    const logo = assetUrl(c("header.v2.logo", "/img/logo-energy-market.png"));

    return (
        <div className="flex h-full flex-col bg-primary text-primary-foreground">
            {/* Logo */}
            <div className="flex items-center gap-2 px-5 h-16 shrink-0 border-b border-white/10">
                <img src={logo} alt="Energy Market" className="h-7 w-auto brightness-0 invert" />
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-accent">Admin</span>
            </div>

            {/* Navigation groupée */}
            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
                {groups.map((g) => (
                    <div key={g.id}>
                        <p className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white/40">{g.label}</p>
                        <ul className="space-y-0.5">
                            {g.modules.map((m) => {
                                const Icon = m.icon;
                                const count = counts?.[m.id] || 0;
                                return (
                                    <li key={m.id}>
                                        <NavLink
                                            to={m.path}
                                            onClick={() => { markSeen?.(m.id); onNavigate?.(); }}
                                            className={({ isActive }) =>
                                                `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                                                    isActive
                                                        ? "bg-accent text-accent-foreground"
                                                        : "text-white/80 hover:bg-white/10 hover:text-white"
                                                }`
                                            }
                                        >
                                            {({ isActive }) => (
                                                <>
                                                    {Icon && <Icon className="h-4 w-4 shrink-0" />}
                                                    <span className="truncate">{m.label}</span>
                                                    {count > 0 && (
                                                        <span
                                                            aria-label={`${count} nouveau${count > 1 ? "x" : ""}`}
                                                            className={`ml-auto shrink-0 rounded-full px-1.5 min-w-[1.25rem] text-center text-[11px] font-bold leading-5 ${
                                                                isActive ? "bg-primary text-white" : "bg-accent text-accent-foreground"
                                                            }`}
                                                        >
                                                            {count > 99 ? "99+" : count}
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </NavLink>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}
            </nav>

            {/* Bas : voir le site + utilisateur + déconnexion */}
            <div className="shrink-0 border-t border-white/10 p-3 space-y-1">
                <Link to="/" onClick={onNavigate} className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-white/70 hover:bg-white/10 hover:text-white">
                    <ExternalLink className="h-4 w-4" /> Voir le site
                </Link>
                <div className="flex items-center gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-semibold">{user?.name || "Admin"}</p>
                        <p className="truncate text-[11px] text-white/50">{user?.email}</p>
                    </div>
                    <button
                        onClick={() => { logout(); window.location.href = "/connexion"; }}
                        title="Se déconnecter"
                        className="rounded-lg p-2 text-white/70 hover:bg-white/10 hover:text-white"
                    >
                        <LogOut className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function AdminLayout() {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const active = findActiveModule(location.pathname);
    const { counts, markSeen } = useActivityCounts();

    // Visiter une section (même par URL directe, pas seulement par clic) doit
    // vider son badge : on marque le module actif comme vu à chaque changement de route.
    useEffect(() => {
        if (active?.id) markSeen(active.id);
    }, [active?.id, markSeen]);

    return (
        <div className="min-h-screen bg-background">
            {/* Sidebar desktop */}
            <aside className="hidden lg:block fixed inset-y-0 left-0 w-60 z-30">
                <SidebarContent counts={counts} markSeen={markSeen} />
            </aside>

            {/* Drawer mobile */}
            {open && (
                <div className="lg:hidden fixed inset-0 z-40">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
                    <div className="absolute inset-y-0 left-0 w-64 shadow-2xl">
                        <button onClick={() => setOpen(false)} className="absolute top-4 right-3 z-10 rounded-lg p-1.5 text-white/80 hover:bg-white/10">
                            <X className="h-5 w-5" />
                        </button>
                        <SidebarContent counts={counts} markSeen={markSeen} onNavigate={() => setOpen(false)} />
                    </div>
                </div>
            )}

            {/* Zone principale */}
            <div className="lg:pl-60">
                {/* Barre supérieure mobile */}
                <div className="lg:hidden sticky top-0 z-20 flex items-center gap-3 h-14 px-4 bg-primary text-primary-foreground">
                    <button onClick={() => setOpen(true)} className="rounded-lg p-1.5 hover:bg-white/10">
                        <Menu className="h-5 w-5" />
                    </button>
                    <span className="font-display font-bold">{active?.label || "Admin"}</span>
                </div>

                <main className="min-h-screen">
                    <ConfirmProvider>
                        <Outlet />
                    </ConfirmProvider>
                </main>
            </div>
        </div>
    );
}
